import React, { useState, useEffect, useCallback } from 'react';
import { usePerformanceMonitor } from '../../utils/performanceMonitor';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  color?: 'primary' | 'white' | 'gray';
  className?: string;
}

const sizeClasses = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
  xl: 'h-8 w-8',
};

const colorClasses = {
  primary: 'border-primary-600 border-t-transparent',
  white: 'border-white border-t-transparent',
  gray: 'border-gray-300 dark:border-gray-700 border-t-primary-600',
};

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  color = 'primary',
  className = '',
}) => {
  return (
    <div
      className={`animate-spin rounded-full border-2 ${sizeClasses[size]} ${colorClasses[color]} ${className}`}
      role='status'
      aria-label='読み込み中'
    >
      <span className='sr-only'>読み込み中...</span>
    </div>
  );
};

interface LoadingButtonProps {
  isLoading: boolean;
  children: React.ReactNode;
  loadingText?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  disabled?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit';
}

export const LoadingButton: React.FC<LoadingButtonProps> = ({
  isLoading,
  children,
  loadingText,
  size = 'md',
  className = '',
  disabled = false,
  onClick,
  type = 'button',
}) => {
  const spinnerSize = size === 'sm' ? 'sm' : 'md';

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isLoading || disabled}
      className={`inline-flex items-center justify-center ${className}`}
    >
      {isLoading && <LoadingSpinner size={spinnerSize} color='white' className='mr-2' />}
      {isLoading && loadingText ? loadingText : children}
    </button>
  );
};

interface LoadingOverlayProps {
  isVisible: boolean;
  message?: string;
  size?: 'sm' | 'md' | 'lg';
  timeoutMs?: number;
  onTimeout?: () => void;
  operationName?: string;
}

interface LoadingWithTimeoutProps {
  isLoading: boolean;
  timeoutMs?: number;
  onTimeout?: () => void;
  operationName?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  color?: 'primary' | 'white' | 'gray';
  className?: string;
  children?: React.ReactNode;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  isVisible,
  message = '読み込み中...',
  size = 'lg',
  timeoutMs,
  onTimeout,
  operationName = 'loading-overlay',
}) => {
  const [showWarning, setShowWarning] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const performanceMonitor = usePerformanceMonitor();

  useEffect(() => {
    if (!isVisible) {
      setShowWarning(false);
      setElapsedTime(0);
      return;
    }

    // パフォーマンス監視開始
    performanceMonitor.startMeasure(operationName, {
      type: 'loading-overlay',
      timeout: timeoutMs,
    });

    const startTime = Date.now();
    const updateInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      setElapsedTime(elapsed);

      // 半分の時間が経過したら警告表示
      if (timeoutMs && elapsed > timeoutMs / 2) {
        setShowWarning(true);
      }
    }, 500);

    let timeoutId: NodeJS.Timeout | undefined;

    if (timeoutMs) {
      timeoutId = setTimeout(() => {
        performanceMonitor.endMeasure(operationName);
        setShowWarning(false);
        if (onTimeout) {
          onTimeout();
        }
      }, timeoutMs);
    }

    return () => {
      clearInterval(updateInterval);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (isVisible) {
        performanceMonitor.endMeasure(operationName);
      }
    };
  }, [isVisible, timeoutMs, onTimeout, operationName, performanceMonitor]);

  if (!isVisible) return null;

  const getDisplayMessage = () => {
    // 警告表示が無効または設定されていない場合は基本メッセージを返す
    if (!showWarning || !timeoutMs) {
      return message;
    }

    // 残り時間を計算（0未満にならないように制限）
    const remainingTime = Math.max(0, timeoutMs - elapsedTime);
    const remainingSeconds = Math.ceil(remainingTime / 1000);

    // 複数行のメッセージを構築
    const warningLines = [
      message,
      '処理に時間がかかっています...',
      `残り時間: ${remainingSeconds}秒`,
    ];

    return warningLines.join('\n');
  };

  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
      <div className='bg-white dark:bg-gray-800 rounded-lg p-6 flex flex-col items-center space-y-4 max-w-sm mx-4'>
        <LoadingSpinner size={size} color='primary' />
        <div className='text-gray-700 dark:text-gray-300 text-center'>
          {getDisplayMessage()
            .split('\n')
            .map((line, index) => (
              <p
                key={index}
                className={index > 0 ? 'text-sm text-yellow-600 dark:text-yellow-400' : ''}
              >
                {line}
              </p>
            ))}
        </div>
        {showWarning && (
          <div className='w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2'>
            <div
              className='bg-yellow-500 h-2 rounded-full transition-all duration-500'
              style={{ width: `${Math.min(100, (elapsedTime / (timeoutMs || 1)) * 100)}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

interface SkeletonProps {
  className?: string;
  width?: string;
  height?: string;
  rounded?: boolean;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  width = 'w-full',
  height = 'h-4',
  rounded = false,
}) => {
  return (
    <div
      className={`animate-pulse bg-gray-200 dark:bg-gray-700 ${width} ${height} ${rounded ? 'rounded-full' : 'rounded'} ${className}`}
      role='status'
      aria-label='読み込み中'
    >
      <span className='sr-only'>読み込み中...</span>
    </div>
  );
};

export const SkeletonCard: React.FC = () => {
  return (
    <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-5 space-y-3'>
      <div className='flex justify-between items-start'>
        <div className='flex-1 space-y-2'>
          <Skeleton width='w-3/4' height='h-5' />
          <Skeleton width='w-1/2' height='h-4' />
        </div>
        <Skeleton width='w-8' height='h-8' rounded />
      </div>
      <div className='space-y-2'>
        <Skeleton width='w-full' height='h-3' />
        <Skeleton width='w-5/6' height='h-3' />
        <Skeleton width='w-4/6' height='h-3' />
      </div>
    </div>
  );
};

export const SkeletonTable: React.FC<{ rows?: number; cols?: number }> = ({
  rows = 5,
  cols = 4,
}) => {
  return (
    <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden'>
      <div className='px-6 py-3 bg-gray-50 dark:bg-gray-700'>
        <div className='grid' style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
          {Array.from({ length: cols }).map((_, i) => (
            <Skeleton key={i} width='w-20' height='h-4' />
          ))}
        </div>
      </div>
      <div className='divide-y divide-gray-200 dark:divide-gray-700'>
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div
            key={rowIndex}
            className='px-6 py-4 grid'
            style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
          >
            {Array.from({ length: cols }).map((_, colIndex) => (
              <Skeleton key={colIndex} width='w-16' height='h-4' />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * タイムアウト対応のローディングコンポーネント
 * 非機能要件のパフォーマンス要件に基づくタイムアウト制御を提供
 */
export const LoadingWithTimeout: React.FC<LoadingWithTimeoutProps> = ({
  isLoading,
  timeoutMs = 5000, // デフォルト5秒（API標準タイムアウト）
  onTimeout,
  operationName = 'loading-operation',
  size = 'md',
  color = 'primary',
  className = '',
  children,
}) => {
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const performanceMonitor = usePerformanceMonitor();

  const handleTimeout = useCallback(() => {
    setHasTimedOut(true);
    if (onTimeout) {
      onTimeout();
    }
  }, [onTimeout]);

  useEffect(() => {
    if (!isLoading) {
      setHasTimedOut(false);
      setShowWarning(false);
      return;
    }

    // パフォーマンス監視開始
    performanceMonitor.startMeasure(operationName, {
      type: 'loading-component',
      timeout: timeoutMs,
    });

    // 半分の時間で警告表示
    const warningTimer = setTimeout(() => {
      setShowWarning(true);
    }, timeoutMs / 2);

    // タイムアウト処理
    const timeoutTimer = setTimeout(() => {
      performanceMonitor.endMeasure(operationName);
      handleTimeout();
    }, timeoutMs);

    return () => {
      clearTimeout(warningTimer);
      clearTimeout(timeoutTimer);
      if (isLoading) {
        performanceMonitor.endMeasure(operationName);
      }
    };
  }, [isLoading, timeoutMs, operationName, performanceMonitor, handleTimeout]);

  if (!isLoading && !hasTimedOut) {
    return <>{children}</>;
  }

  if (hasTimedOut) {
    return (
      <div className={`flex items-center space-x-2 text-red-600 dark:text-red-400 ${className}`}>
        <svg className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
          />
        </svg>
        <span className='text-sm'>処理がタイムアウトしました</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <LoadingSpinner size={size} color={color} />
      <span
        className={`text-sm ${showWarning ? 'text-yellow-600 dark:text-yellow-400' : 'text-gray-600 dark:text-gray-400'}`}
      >
        {showWarning ? '処理に時間がかかっています...' : '読み込み中...'}
      </span>
    </div>
  );
};

/**
 * 配車計算専用のローディングコンポーネント
 * 30秒のタイムアウト制御付き
 */
export const DispatchLoadingOverlay: React.FC<{
  isVisible: boolean;
  onTimeout?: () => void;
}> = ({ isVisible, onTimeout }) => {
  return (
    <LoadingOverlay
      isVisible={isVisible}
      message='AI配車計算を実行中...'
      size='lg'
      timeoutMs={30000} // 30秒タイムアウト
      onTimeout={onTimeout}
      operationName='dispatch-calculation-overlay'
    />
  );
};

/**
 * API呼び出し専用のローディングコンポーネント
 * 5秒のタイムアウト制御付き
 */
export const ApiLoadingOverlay: React.FC<{
  isVisible: boolean;
  message?: string;
  onTimeout?: () => void;
}> = ({ isVisible, message = 'データを取得中...', onTimeout }) => {
  return (
    <LoadingOverlay
      isVisible={isVisible}
      message={message}
      size='lg'
      timeoutMs={5000} // 5秒タイムアウト
      onTimeout={onTimeout}
      operationName='api-call-overlay'
    />
  );
};

export default LoadingSpinner;
